import { v4 as uuid } from 'uuid';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlContainer, UntypedFormControl, NgForm } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { ApiClientConstant, Models, RequestQueryPayload, Table } from '@cureskin/api-client';
import { ConnectionService } from '../../../services/connection-service';
import { InputType } from '../../../../typings/client/input';
import { WindowRefService } from '../../../services/window-ref-service';
import { Broadcaster } from '../../broadcaster';
import { AppConfig } from '../../../app/app.config';

/**
 * Same UX as <language-string-selection> (search / create English-only / edit-icon) but works with a
 * PLAIN object as `parseObj`: it reads the current value (a LanguageString objectId string, or an
 * expanded `{ objectId, en, ... }` JSON) and always writes back the LanguageString objectId string to
 * `parseObj[name]`. This suits JSON API payloads where the field is stored as an objectId reference.
 * When `previewImage` is set, the English value is treated as an image URL and previewed inline.
 */
@Component({
  selector: 'language-string-object-selection',
  templateUrl: './language-string-object-selection.html',
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  standalone: false,
})
export class LanguageStringObjectSelectionComponent {
  @Input('languageStringObject')
  set onUpdateLanguageStringObject(languageStringObject: Record<string, unknown>) {
    this.languageStringObject = languageStringObject;
    this.syncFromLanguageStringObject();
  }
  @Input('name')
  set onUpdateName(name: string) {
    this.name = name;
    this.syncFromLanguageStringObject();
  }
  @Input('tags')
  set onUpdateTags(tags: string | Array<string>) {
    let list: Array<string> = [];
    if (tags) list = tags instanceof Array ? tags : [tags];
    void this.fetchTags(list);
  }
  @Input('required') required: boolean = false;
  @Input('prefix') prefix: string;
  @Input('placeholder') placeholder: string = '';
  @Input('previewImage') previewImage: boolean = false;
  @Output('selectionChange') selectionChange: EventEmitter<string> = new EventEmitter<string>();

  searchControl: UntypedFormControl = new UntypedFormControl();
  showSearch: boolean = false;
  name: string = uuid();
  languageStringObject: Record<string, unknown>;
  selectedId: string = '';
  selectedText: string = '';
  options: Array<InputType.SelectOption> = [];
  tags: Array<Table.LanguageStringTag> = [];
  tagPlaceholder: string = '';
  currentUser: Table.User;

  constructor(private conn: ConnectionService,
    private windowService: WindowRefService,
    private broadcaster: Broadcaster,
    public appConfig: AppConfig) {
  }

  ngOnInit(): void {
    this.currentUser = this.conn.getCurrentUser();
    this.searchControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe((): void => { void this.updateOptions(); });
  }

  private syncFromLanguageStringObject(): void {
    if (!this.languageStringObject || !this.name) return;
    const value: unknown = this.languageStringObject[this.name];
    if (!value) {
      this.selectedId = '';
      this.selectedText = '';
      return;
    }
    if (typeof value === 'string') {
      this.selectedId = value;
      if (!this.selectedText) void this.loadSelectedText(value);
    } else {
      const languageString: Models.LanguageString = value as Models.LanguageString;
      this.selectedId = languageString.objectId || '';
      this.selectedText = languageString[ApiClientConstant.LanguageString.EN] || '';
    }
    // Normalise the bound field to the objectId string so the save payload is always a reference.
    this.languageStringObject[this.name] = this.selectedId || undefined;
    void this.updateOptions();
  }

  private async loadSelectedText(id: string): Promise<void> {
    const languageStrings: Array<Table.LanguageString> = await this.conn
      .findLanguageStrings({ where: { objectId: id }, project: [ApiClientConstant.LanguageString.EN], limit: 1 });
    const languageString: Table.LanguageString = languageStrings[0];
    if (languageString) this.selectedText = languageString.get(ApiClientConstant.LanguageString.EN);
  }

  private async fetchTags(tags: Array<string>): Promise<void> {
    this.tags = await this.conn.fetchLanguageStringTags({ tags });
    this.tagPlaceholder = tags.map((tag: string): string => `"${tag}"`).join(', ');
    await this.updateOptions();
  }

  async updateOptions(): Promise<void> {
    const payload: RequestQueryPayload<Table.LanguageString> = {
      where: {}, project: [ApiClientConstant.LanguageString.EN], limit: 100,
    };
    if (this.tags?.length) payload.where.tags = this.tags;
    const search: string = this.searchControl.value || this.selectedText;
    if (search) payload.where.en = { $regex: search, $options: 'i' };
    const languageStrings: Array<Table.LanguageString> = await this.conn.findLanguageStrings(payload);
    this.options = languageStrings.map((languageString: Table.LanguageString): InputType.SelectOption => ({
      display: languageString.get(ApiClientConstant.LanguageString.EN),
      value: languageString.id,
    }));
    if (this.selectedId && this.options.every((option: InputType.SelectOption): boolean => option.value !== this.selectedId)) {
      this.options.unshift({ display: this.selectedText || this.selectedId, value: this.selectedId });
    }
  }

  onSelect(): void {
    const option: InputType.SelectOption = this.options
      .find((each: InputType.SelectOption): boolean => each.value === this.selectedId);
    if (option) this.selectedText = `${option.display}`;
    if (this.languageStringObject && this.name) this.languageStringObject[this.name] = this.selectedId || undefined;
    this.selectionChange.emit(this.selectedId);
  }

  async addLanguageString(): Promise<void> {
    if (!this.searchControl.value) return;
    try {
      const languageString: Table.LanguageString = new Table.LanguageString();
      languageString.set('tags', this.tags);
      languageString.set(ApiClientConstant.LanguageString.EN, this.searchControl.value);
      languageString.set('createdBy', this.currentUser);
      await languageString.save();
      this.selectedId = languageString.id;
      this.selectedText = this.searchControl.value;
      if (this.languageStringObject && this.name) this.languageStringObject[this.name] = this.selectedId;
      this.selectionChange.emit(this.selectedId);
      this.showSearch = false;
      this.searchControl.reset();
      await this.updateOptions();
    } catch (error) {
      this.broadcaster.broadcast('NOTIFY', { message: error.message, type: this.appConfig.Shared.Toast.Type.ERROR });
    }
  }

  edit(): void {
    if (!this.selectedId) return;
    this.windowService.nativeWindow.open(`${this.conn.getBaseUrl()}/languageString/${this.selectedId}/edit`);
  }
}
